import React, { useEffect, useState } from "react";
import { getApiBase, getBackendUrl } from "../utils/paths";
import styles from "./DonationPopup.module.css";

/* ---- inline icons (no extra deps) ---- */
const HeartIcon = ({ size = 16, color = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M12 21s-6.7-4.35-9.3-8.1C.9 10.1 1.6 6.4 4.7 5.1 7 4.1 9.4 5 12 7.7 14.6 5 17 4.1 19.3 5.1c3.1 1.3 3.8 5 1.99 7.8C18.7 16.65 12 21 12 21Z" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2">
    <path d="M12 3 4 6v6c0 5 3.5 8.5 8 9 4.5-.5 8-4 8-9V6l-8-3Z" strokeLinejoin="round" />
    <path d="m9 12 2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BadgeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2">
    <circle cx="12" cy="8" r="5.5" />
    <path d="M8.5 13 7 21l5-2.5L17 21l-1.5-8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const HandsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2">
    <path d="M12 20.5s-6-3.7-8.3-7.2C2.3 11.3 3 8.4 5.4 7.5c1.8-.7 3.6 0 5.2 2 .3.4.9.4 1.2 0 1.6-2 3.4-2.7 5.2-2 2.4.9 3.1 3.8 1.7 5.8C18.4 16.8 12 20.5 12 20.5Z" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
    <path d="M6 6 18 18M18 6 6 18" strokeLinecap="round" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
    <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DonationPopup = ({ show, onClose }) => {
  const [showForm, setShowForm] = useState(false);
  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [formSuccess, setFormSuccess] = useState("");
  const [bankDetails, setBankDetails] = useState(null);
  const [bankLoading, setBankLoading] = useState(true);
  const [bankError, setBankError] = useState("");
  const API_BASE = getApiBase();

  useEffect(() => {
    let isMounted = true;
    const fetchBankDetails = async () => {
      try {
        setBankLoading(true);
        setBankError("");
        const res = await fetch(getBackendUrl('/backend/api/public/bank_details.php'));
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error(body?.error || 'Unable to load bank details');
        }
        const data = await res.json();
        if (isMounted) {
          setBankDetails(data.item || null);
        }
      } catch (error) {
        if (isMounted) {
          setBankError(error.message || 'Failed to load bank details');
        }
      } finally {
        if (isMounted) {
          setBankLoading(false);
        }
      }
    };

    if (show) {
      fetchBankDetails();
    }

    return () => {
      isMounted = false;
    };
  }, [API_BASE, show]);

  if (!show) return null;

  const handleOpenForm = () => setShowForm(true);

  const handleClose = () => {
    setShowForm(false);
    setFormError("");
    setFormSuccess("");
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError("");
    setFormSuccess("");

    const formData = new FormData(event.target);
    formData.set('full_name', formData.get('full_name') || formData.get('fullName') || '');
    formData.set('email', formData.get('email') || '');
    formData.set('phone', formData.get('phone') || formData.get('mobile') || '');
    formData.set('address', formData.get('address') || '');
    formData.set('pan_number', formData.get('pan_number') || formData.get('pan') || '');
    formData.set('donation_reason', formData.get('donation_reason') || formData.get('reason') || '');
    formData.set('donation_mode', formData.get('donation_mode') || formData.get('mode') || '');
    formData.set('utr_number', formData.get('utr_number') || formData.get('utr') || '');
    formData.set('donation_amount', formData.get('donation_amount') || formData.get('amount') || '0');

    setFormLoading(true);
    try {
      const response = await fetch(getBackendUrl('/backend/api/public/donations.php'), {
        method: 'POST',
        body: formData,
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.error || data?.message || 'Donation submission failed');
      }

      setFormSuccess(data?.message || 'Your donation request has been submitted successfully.');
      event.target.reset();
      setShowForm(false);
      onClose();
    } catch (error) {
      setFormError(error.message || 'Failed to submit donation');
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div className={styles.popupOverlay} onClick={handleClose}>
      <div
        className={`${styles.popupBox} ${showForm ? styles.popupBoxForm : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button className={styles.closeBtn} onClick={handleClose} aria-label="Close">
          <CloseIcon />
        </button>

        {!showForm ? (
          <div className={styles.ctaLayout}>
            <div className={styles.ctaVisual}>
              <div className={`${styles.blob} ${styles.blob1}`} />
              <div className={`${styles.blob} ${styles.blob2}`} />
              <div className={`${styles.dots} ${styles.dotsTl}`} />
              <div className={`${styles.dots} ${styles.dotsBr}`} />
              <div className={styles.visualHeart}>
                <HeartIcon size={72} color="#ffffff" />
                <div className={styles.visualOrgName}>Vikash Organization</div>
                <div className={styles.visualOrgTag}>Bringing hope to everyone</div>
              </div>
            </div>

            <div className={styles.ctaContent}>
              <span className={styles.popupBadge}>
                <HeartIcon size={13} color="#16a34a" /> Donation invitation
              </span>
              <h2 className={styles.popupTitle}>
                Give hope today with a <span className={styles.popupTitleAccent}>secure donation.</span>
              </h2>
              <p className={styles.popupText}>
                Click Donate Now to view the full contribution form and submit your payment details securely.
              </p>

              <div className={styles.divider}>
                <span className={styles.dividerLine} />
                <span className={styles.dividerIcon}><HandsIcon /></span>
                <span className={styles.dividerLine} />
              </div>

              <div className={styles.featureGrid}>
                <div className={styles.featureCard}>
                  <span className={styles.featureIcon}><ShieldIcon /></span>
                  <div>
                    <p className={styles.featureTitle}>100% secure</p>
                    <p className={styles.featureDesc}>Your donation is safe with us</p>
                  </div>
                </div>
                <div className={styles.featureCard}>
                  <span className={styles.featureIcon}><BadgeIcon /></span>
                  <div>
                    <p className={styles.featureTitle}>Trusted NGO</p>
                    <p className={styles.featureDesc}>Transparent and accountable</p>
                  </div>
                </div>
                <div className={styles.featureCard}>
                  <span className={styles.featureIcon}><HandsIcon /></span>
                  <div>
                    <p className={styles.featureTitle}>Make impact</p>
                    <p className={styles.featureDesc}>Your support changes lives</p>
                  </div>
                </div>
              </div>

              <div className={styles.popupActions}>
                <button type="button" className={`${styles.popupBtn} ${styles.popupBtnPrimary}`} onClick={handleOpenForm}>
                  <HeartIcon size={15} color="#ffffff" /> Donate now <ArrowIcon />
                </button>
                <button type="button" className={`${styles.popupBtn} ${styles.popupBtnSecondary}`} onClick={handleClose}>
                  <CloseIcon /> No thanks, close
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.popupBody}>
            <div className={styles.donationModalContent}>
              <div className={styles.donationPopupHeader}>
                <div>
                  <span className={styles.popupBadge}>
                    <HeartIcon size={13} color="#16a34a" /> Donation form
                  </span>
                  <h2 className={`${styles.popupTitle} ${styles.popupTitleSm}`}>Complete your donation securely below.</h2>
                </div>
                <div className={styles.popupNote}>Secure offline donation</div>
              </div>

              <form className={styles.donationForm} onSubmit={handleSubmit} encType="multipart/form-data">
                <div className={`${styles.donationCard} ${styles.donationCardDetails}`}>
                  <div className={styles.cardHeading}>
                    <span>Bank account details</span>
                  </div>
                  <div className={styles.accountItem}>
                    <strong>Account Name:</strong>
                    <span>{bankDetails?.account_name || ''}</span>
                  </div>
                  <div className={styles.accountItem}>
                    <strong>Account Number:</strong>
                    <span>{bankDetails?.account_number || ''}</span>
                  </div>
                  <div className={styles.accountItem}>
                    <strong>IFSC Code:</strong>
                    <span>{bankDetails?.ifsc_code || ''}</span>
                  </div>
                  <div className={styles.accountItem}>
                    <strong>Bank:</strong>
                    <span>{bankDetails?.bank_name || ''}</span>
                  </div>
                  <div className={styles.accountItem}>
                    <strong>Branch:</strong>
                    <span>{bankDetails?.branch_name || ''}</span>
                  </div>
                  <div className={styles.accountItem}>
                    <strong>UPI ID:</strong>
                    <span>{bankDetails?.upi_id || ''}</span>
                  </div>

                  <div className={styles.qrCard}>
                    <div className={styles.qrTitle}>Pay via QR code</div>
                    <div className={styles.qrImage}>
                      <img
                        src={bankDetails?.qr_code_path ? getBackendUrl(bankDetails.qr_code_path) : ""}
                        alt="UPI QR Code for donation"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '12px',
                          display: 'block'
                        }}
                        onError={(e) => {
                          e.target.parentElement.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#16a34a;font-weight:bold;font-size:1.2rem;">QR Code Not Found</div>';
                        }}
                      />
                    </div>
                    <p className={styles.qrCaption}>Scan with any UPI app to pay</p>
                  </div>

                  <ul className={styles.benefitsList}>
                    <li>{bankDetails?.secure_offline_text || 'Secure offline donation'}</li>
                    <li>{bankDetails?.tax_benefit_text || 'Eligible for 80G tax benefits'}</li>
                    <li>{bankDetails?.transparent_text || 'Transparent fund utilization'}</li>
                  </ul>
                  {bankLoading && <div className={styles.formNote}>Loading bank details...</div>}
                  {bankError && <div className={styles.formError}>{bankError}</div>}
                </div>

                <div className={`${styles.donationCard} ${styles.donationCardForm}`}>
                  <div className={styles.cardHeading}>
                    <span>Donation details</span>
                  </div>
                  <div className={styles.formRow}>
                    <label>
                      Full Name
                      <input type="text" name="full_name" placeholder="Full Name" required />
                    </label>
                    <label>
                      Mobile Number
                      <input type="tel" name="phone" placeholder="Mobile Number" required />
                    </label>
                  </div>
                  <label>
                    Email (Optional)
                    <input type="email" name="email" placeholder="Email (Optional)" />
                  </label>
                  <label>
                    Address
                    <textarea name="address" placeholder="Address" rows="3" required />
                  </label>
                  <label>
                    PAN Card Number
                    <input type="text" name="pan_number" placeholder="PAN Card Number" required />
                  </label>
                  <label>
                    Reason of Donation
                    <select name="donation_reason" required>
                      <option value="">Select Reason</option>
                      <option value="education">Education Support</option>
                      <option value="health">Health &amp; Medical</option>
                      <option value="livelihood">Livelihood</option>
                      <option value="general">General Donation</option>
                    </select>
                  </label>
                  <label>
                    Select Mode
                    <select name="donation_mode" required>
                      <option value="">Select Mode</option>
                      <option value="bank">Bank Transfer</option>
                      <option value="upi">UPI</option>
                      <option value="cash">Cash Deposit</option>
                    </select>
                  </label>
                  <label>
                    UTR Number
                    <input type="text" name="utr_number" placeholder="UTR Number" required />
                  </label>
                  <label className={styles.fileLabel}>
                    Payment Screenshot (Required)
                    <input type="file" name="screenshot" accept="image/*" required />
                  </label>
                  <label>
                    Donation Amount
                    <input type="number" name="donation_amount" placeholder="Amount" min="1" required />
                  </label>

                  {formError && <div className={styles.formError}>{formError}</div>}
                  {formSuccess && <div className={styles.formSuccess}>{formSuccess}</div>}
                  <button type="submit" className={`${styles.popupBtn} ${styles.popupBtnPrimary} ${styles.submitBtn}`} disabled={formLoading}>
                    <HeartIcon size={15} color="#ffffff" /> {formLoading ? 'Submitting...' : 'Submit donation'}
                  </button>
                  <button type="button" className={`${styles.popupBtn} ${styles.popupBtnSecondary}`} onClick={handleClose}>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DonationPopup;